<template>
  <section
    class="my-8 md:mb-8 mx-auto bg-black pt-6 px-2 md:px-10 pb-0 shadow-black shadow-xl divide-y-2 divide-white font-helvetica text-[22px] text-[#999999] max-w-[1400px]"
  >
    <RallyLogoDownload />

    <!-- Intro -->
    <div class="pt-8 mt-8 flex flex-col gap-4">
      <h1 class="text-white text-2xl md:text-4xl font-russo">
        Rally Navigator Pro Features
      </h1>
      <p class="text-[17px] md:text-[20px]">
        Pro Rally is the commercial tier of Rally Navigator with advanced
        features. Pro adds the licensing you need to run paid events, the
        branding controls that make a rally roadbook proper, official event
        documentation, and advanced mapping, waypoint and export tools. Every
        feature below is included in the Pro Rally subscription.
      </p>
    </div>

    <!-- Jump list: every Pro feature, linked to its section below -->
    <div class="py-8 mt-8 rn-pricing app-card mx-auto">
      <ProRallyCard />
    </div>

    <!-- Commercial use and licensing -->
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo">
        Commercial Use &amp; Licensing
      </h2>

      <h3 id="pro-license" class="text-white text-xl font-russo scroll-mt-[90px] md:scroll-mt-4">
        Pro License for Commercial Rally Events
      </h3>
      <p class="text-[17px] md:text-[20px]">
        Pro Subscribers have the right to use the roadbooks you create in
        commercial events — events with paid entries and any commercial
        activity. The Premium tier is licensed for personal use only, not
        commercial events. Pro level is required once money is involved in the
        event.
      </p>
      <hr class="border-0 border-t border-white/50 my-2" />

      <h3 id="commercial-use" class="text-white text-xl font-russo scroll-mt-[90px] md:scroll-mt-4">
        Removes &lsquo;Not for Commercial Use&rsquo;
      </h3>
      <p class="text-[17px] md:text-[20px]">
        Roadbooks produced on the Premium tier carry a &ldquo;Not for Commercial
        Use&rdquo; marking in the PDF header. Pro removes it, so the PDFs and
        exports you hand to competitors and officials carry your event branding
        and nothing else.
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/premium-personal-use-banner.37b81a50.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="372"
          height="323"
          src="/images/pro-features/premium-personal-use-banner.37b81a50.png"
          class="flex m-auto"
          alt="A Premium roadbook header reading Premium - Personal Use Only, above a warning block and a highlighted For Personal Use Only, Not for Commercial Use footer"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        The Premium markings that Pro removes
      </p>
    </div>

    <!-- Roadbook branding and print output -->
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo">
        Roadbook Branding &amp; Print Output
      </h2>

      <h3 id="header-image" class="text-white text-xl font-russo scroll-mt-[90px] md:scroll-mt-4">Custom Roadbook Header Image</h3>
      <p class="text-[17px] md:text-[20px]">
        Select a custom header image at the top of every roadbook page with
        your own event artwork, series or sponsor banner.
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/header-image-choose.24980068.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="1243"
          height="645"
          src="/images/pro-features/header-image-choose.24980068.png"
          class="block mx-auto w-full max-w-[1100px] h-auto"
          alt="Rally Navigator with Choose Header Image selected in the Roadbook menu and a Trinite Road Rally banner loaded as the roadbook header"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        Choosing a custom header image
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/header-image-crop-tool.ad5e1c5b.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="548"
          height="194"
          src="/images/pro-features/header-image-crop-tool.ad5e1c5b.png"
          class="flex m-auto"
          alt="A header image in the built-in crop tool with crop handles around the artwork and the Save cropped image button highlighted"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        Built-in crop tool for header image
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/header-image-right-click.20b8e241.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="539"
          height="551"
          src="/images/pro-features/header-image-right-click.20b8e241.png"
          class="flex m-auto"
          alt="Right-clicking the roadbook header brings up Choose Header Image and Remove Header Image"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        Right-click the header to change or remove the image
      </p>
      <hr class="border-0 border-t border-white/50 my-2" />

      <h3 id="qr-code" class="text-white text-xl font-russo scroll-mt-[90px] md:scroll-mt-4">
        Custom QR Code in Header &amp; Waypoints
      </h3>
      <p class="text-[17px] md:text-[20px]">
        Place QR codes on the roadbook header and on individual waypoints.
        Competitors can scan them in the field to reach whatever URL you desire.
      </p>
      <ul
        class="flex flex-row flex-wrap justify-center gap-x-10 gap-y-2 py-2 font-arial text-[16px] md:text-[18px] font-bold"
      >
        <li>Event information</li>
        <li>Hotel location</li>
        <li>Download a PDF</li>
        <li>Emergency contact</li>
      </ul>
      <picture class="contents">
        <source
          srcset="/images/pro-features/qr-menu-add.84ef2763.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="372"
          height="436"
          src="/images/pro-features/qr-menu-add.84ef2763.png"
          class="flex m-auto"
          alt="Rally Navigator Roadbook menu with QR Code in Header selected, showing the Add, Change and Delete options"
        />
      </picture>
      <picture class="contents">
        <source
          srcset="/images/pro-features/qr-header-add.2508d3b9.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="534"
          height="390"
          src="/images/pro-features/qr-header-add.2508d3b9.png"
          class="flex m-auto"
          alt="Roadbook header with a custom URL placeholder and the prompt to click to add a custom QR code"
        />
      </picture>
      <picture class="contents">
        <source
          srcset="/images/pro-features/qr-header-result.5fb6ebbf.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="539"
          height="393"
          src="/images/pro-features/qr-header-result.5fb6ebbf.png"
          class="flex m-auto"
          alt="The finished roadbook header with the QR code rendered beside the stage description and distance details"
        />
      </picture>
      <picture class="contents">
        <source
          srcset="/images/pro-features/qr-waypoint-url-icon.b5fd365e.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="539"
          height="407"
          src="/images/pro-features/qr-waypoint-url-icon.b5fd365e.png"
          class="flex m-auto"
          alt="The URL icon in the icon toolbar being placed on a waypoint, producing a scannable QR code in that roadbook cell"
        />
      </picture>
      <hr class="border-0 border-t border-white/50 my-2" />

      <h3 id="custom-icons" class="text-white text-xl font-russo scroll-mt-[90px] md:scroll-mt-4">
        Import Custom Navigation Icons
      </h3>
      <p class="text-[17px] md:text-[20px]">
        Load your own navigation symbols alongside the built-in FIA and Dakar
        icon sets. Useful for national federations with their own symbol
        conventions, or for hazards and instructions the standard sets do not
        cover.
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/icons-add-to-library.1bcda87d.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="158"
          height="112"
          src="/images/pro-features/icons-add-to-library.1bcda87d.png"
          class="flex m-auto"
          alt="The Icons menu open with Add Icon To Library, Import Icon Library and Export Icon Library options"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        Add icons to the library from the Icons menu
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/icons-my-icons-tab.f8119ac2.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="542"
          height="282"
          src="/images/pro-features/icons-my-icons-tab.f8119ac2.png"
          class="flex m-auto"
          alt="The My Icons 1 tab in the icon toolbar with the plus button and its Add Icons Pro tooltip, below a roadbook entry"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        Or add them straight from the My Icons tab
      </p>
      <p class="text-[17px] md:text-[20px]">
        You can also copy an icon image from a web browser and paste it into a
        waypoint, then right-click it and choose Copy to My Icons to save it to
        your library.
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/icons-copy-to-my-icons.6ea1cac7.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="533"
          height="261"
          src="/images/pro-features/icons-copy-to-my-icons.6ea1cac7.png"
          class="flex m-auto"
          alt="Right-clicking a stop sign icon on a waypoint and choosing Copy to My Icons, which adds it to the My Icons library"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        Right-click a waypoint icon and choose Copy to My Icons
      </p>
      <p class="text-[17px] md:text-[20px]">
        Pro users also get six additional icon tabs, so custom icons can be
        organised into whatever groupings suit the event.
      </p>
      <picture class="contents">
        <source srcset="/images/pro-features/icon-tabs-six.b09f7f21.webp" type="image/webp" />
        <img
          decoding="async"
          loading="lazy"
          width="544"
          height="146"
          src="/images/pro-features/icon-tabs-six.b09f7f21.png"
          class="flex m-auto"
          alt="The icon toolbar showing the My Icons 1 to My Icons 6 tabs, with a row of imported custom icons below"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        Six My Icons tabs for organising your own icons
      </p>
      <p class="text-[17px] md:text-[20px]">
        Right-click a tab to rename it, dock the panel to the right, or grow and
        shrink the icons.
      </p>
      <picture class="contents">
        <source srcset="/images/pro-features/icon-tab-rename.ceabf6fa.webp" type="image/webp" />
        <img
          decoding="async"
          loading="lazy"
          width="548"
          height="185"
          src="/images/pro-features/icon-tab-rename.ceabf6fa.png"
          class="flex m-auto"
          alt="Right-clicking a My Icons tab opens a menu with Rename Icon Folder, Dock To Right, Shrink Icons and Grow Icons"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        Right-click a tab to rename it
      </p>
      <hr class="border-0 border-t border-white/50 my-2" />

      <h3 id="moto-rolls" class="text-white text-xl font-russo scroll-mt-[90px] md:scroll-mt-4">Multi-Wide Moto Rolls</h3>
      <p class="text-[17px] md:text-[20px]">
        Print to PDF continuous motorcycle roadbook rolls in multiple copies per
        wide format sheet so the same route can be printed on a wide format
        printer and cut to width to produce multiple rolls from a single sheet
        of paper. Ask your commercial printer about the proper paper width and
        the max rolls available for that sheet size.
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/roll-width-setting.bd3bb390.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="467"
          height="524"
          src="/images/pro-features/roll-width-setting.bd3bb390.png"
          class="flex m-auto"
          alt="Rally Navigator PDF settings with paper size set to Roll and the Roll Width menu open, showing options 1 through 8"
        />
      </picture>
      <picture class="contents">
        <source
          srcset="/images/pro-features/multi-wide-moto-rolls.d242b0aa.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="1457"
          height="627"
          src="/images/pro-features/multi-wide-moto-rolls.d242b0aa.png"
          class="block mx-auto w-full max-w-[1100px] h-auto"
          alt="One wide-format sheet holding six identical Cross Country Rally Sample roadbook rolls side by side, ready to be cut to width"
        />
      </picture>
      <p class="text-[17px] md:text-[20px] text-center">
        <a
          href="/create-cross-country/Cross_Country_Rally_Sample-Roll-x6.pdf"
          target="_blank"
          rel="noopener"
          class="text-[#FF0000] hover:underline"
          ><strong>Download the sample &mdash; Moto Style Roll x 6, Cross Country
          Rally on 36&#8243; paper (PDF)</strong></a
        >
      </p>
      <hr class="border-0 border-t border-white/50 my-2" />

      <h3 id="zone-controls" class="text-white text-xl font-russo scroll-mt-[90px] md:scroll-mt-4">
        Regroup, Service, Zone Control
      </h3>
      <p class="text-[17px] md:text-[20px]">
        Road Rally format now supports Time Control Zones for Regroup, Service
        Park, Remote Service, Parc Fermé, Flexi Service, Tyre Fitting and
        Technical Zones, or a custom zone name of your own. Marking a Regroup Out
        starts a new section header and a page break, which resets the distance
        and waypoint numbering per FIA rules.
      </p>
      <picture class="contents">
        <source srcset="/images/pro-features/zone-controls.ee1407d9.webp" type="image/webp" />
        <img
          decoding="async"
          loading="lazy"
          width="1095"
          height="644"
          src="/images/pro-features/zone-controls.ee1407d9.png"
          class="block mx-auto w-full max-w-[1100px] h-auto"
          alt="The Time Control Properties dialog with Regroup set to Out and Starts A New Section And Page ticked, beside roadbook rows showing TC 2 Regroup In and TC 3 Regroup Out"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        Zone Out starts a new section and resets the numbering
      </p>
    </div>

    <!-- Mapping and route data -->
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo">
        Mapping &amp; Route Data
      </h2>

      <h3 id="map-sources" class="text-white text-xl font-russo scroll-mt-[90px] md:scroll-mt-4">Additional Map Sources</h3>
      <p class="text-[17px] md:text-[20px]">
        Unlocks additional base map and satellite imagery layers. Coverage and
        imagery may vary between providers, so having more to choose from may
        give you better options in your area.
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/map-sources-menu.c7634947.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="579"
          height="599"
          src="/images/pro-features/map-sources-menu.c7634947.png"
          class="flex m-auto"
          alt="The Map menu with Map Source open; the Pro-only providers are boxed in red, from Google Terrain and IGN France through OpenTopoMap, ESRI World Imagery, USGS Topo and My Custom Maps"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        The map sources Pro unlocks, boxed in red
      </p>
      <hr class="border-0 border-t border-white/50 my-2" />

      <h3 id="speed-elevation" class="text-white text-xl font-russo scroll-mt-[90px] md:scroll-mt-4">
        Show Speed &amp; Elevation on Map
      </h3>
      <p class="text-[17px] md:text-[20px]">
        Display speed limit and elevation information at the selected location
        on the map while planning your route.
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/speed-elevation-on-map.e790a295.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="1288"
          height="490"
          src="/images/pro-features/speed-elevation-on-map.e790a295.png"
          class="block mx-auto w-full max-w-[1100px] h-auto"
          alt="The Map menu with Show Speed Limit on Map and Show Elevation on Map enabled, showing a 45 MPH speed limit and 6234 feet elevation readout at the selected point"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        Speed limit and elevation at the selected location
      </p>
      <hr class="border-0 border-t border-white/50 my-2" />

      <h3 id="track-color" class="text-white text-xl font-russo scroll-mt-[90px] md:scroll-mt-4">Show Track Color On Map</h3>
      <p class="text-[17px] md:text-[20px]">
        The track on the map will change color depending on special stage,
        transit stage, speed zone, neutralization, and neutralization with speed
        limit.
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/track-color-on-map.b77f4fb5.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="1217"
          height="718"
          src="/images/pro-features/track-color-on-map.b77f4fb5.png"
          class="block mx-auto w-full max-w-[1100px] h-auto"
          alt="Show Track Color On Map enabled in the Map menu, with the route drawn in different colours for each stage type alongside the matching roadbook entries"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        Show Track Color On Map
      </p>
      <hr class="border-0 border-t border-white/50 my-2" />

      <h3 id="elevation-profile" class="text-white text-xl font-russo scroll-mt-[90px] md:scroll-mt-4">Elevation Profile &amp; Data</h3>
      <p class="text-[17px] md:text-[20px]">
        Generate elevation profile for the route on screen and in PDF. Also
        includes elevation data in the OpenRallyGPX export. The elevation
        profile now shows blue for transit stage, red for special stage, plus
        start, finish and fuel icons along the route. All of it is exported to
        the matching PDF.
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/elevation-profile-app.480a061c.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="1243"
          height="530"
          src="/images/pro-features/elevation-profile-app.480a061c.png"
          class="block mx-auto w-full max-w-[1100px] h-auto"
          alt="Show Elevation Profile selected in the Roadbook menu, with the profile above the roadbook drawn blue for transit and red for special stage, marked with start, fuel and finish icons"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        Show Elevation Profile in the app
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/elevation-profile-pdf.18b5cfa6.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="550"
          height="570"
          src="/images/pro-features/elevation-profile-pdf.18b5cfa6.png"
          class="flex m-auto"
          alt="The printed roadbook header page carrying the same coloured elevation profile with its start, fuel and finish icons and altitude figures"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        The same profile printed in the roadbook PDF
      </p>
      <hr class="border-0 border-t border-white/50 my-2" />

      <h3 id="street-view" class="text-white text-xl font-russo scroll-mt-[90px] md:scroll-mt-4">
        Street View Image to Roadbook
      </h3>
      <p class="text-[17px] md:text-[20px]">
        Add the current Street View image of a road section directly to the Roadbook notes box.
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/street-view-to-notes-box.ee3967b4.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="1015"
          height="722"
          src="/images/pro-features/street-view-to-notes-box.ee3967b4.png"
          class="block mx-auto w-full max-w-[1100px] h-auto"
          alt="Street View Image to Text Box selected in the Waypoint menu, placing the Street View photograph of the road into the roadbook notes box"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        Street View Image to Text Box, from the Waypoint menu
      </p>
      <hr class="border-0 border-t border-white/50 my-2" />

      <h3 id="gps-recorder" class="text-white text-xl font-russo scroll-mt-[90px] md:scroll-mt-4">GPS Roadbook Recorder LIVE</h3>
      <p class="text-[17px] md:text-[20px]">
        Record a Roadbook and save Waypoints LIVE from GPS location while you
        drive the route. Distances, headings and waypoints are captured in the
        field. You can do complete waypoint editing and details as you drive and
        come back with a Roadbook to refine at the desk rather than one to build
        from scratch.
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/gps-recorder-bar.378f6280.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="743"
          height="87"
          src="/images/pro-features/gps-recorder-bar.378f6280.png"
          class="flex m-auto"
          alt="The recorder bar while recording: green GPS dot, REC indicator, elapsed time, distance, speed, and the New Waypoint, Pause and Stop controls"
        />
      </picture>
      <p class="text-[17px] md:text-[20px]">
        This feature requires USB GPS input to your laptop computer. Tested for
        Mac and PC with the
        <a
          href="https://www.globalsat.com.tw/en/product-289511/USB-C-GPS-Receiver-BU-353NC.html"
          target="_blank"
          rel="noopener"
          class="text-[#FF0000] hover:underline"
          >GlobalSat <strong>BU-353NC</strong></a
        >
        and the V-FAN <strong>UG-353</strong>. Rally Navigator connects over a
        standard USB serial connection, so other USB GPS receivers may also
        work; these two are the units we have tested. Not tested on Linux — it
        would be great if a Linux user could test and confirm.
      </p>
      <div
        class="flex flex-col md:flex-row gap-4 justify-center items-start max-w-[900px] mx-auto w-full"
      >
        <picture class="contents">
          <source
            srcset="/images/pro-features/gps-receivers-top.1db52c1c.webp"
            type="image/webp"
          />
          <img
            decoding="async"
            loading="lazy"
            width="1024"
            height="582"
            src="/images/pro-features/gps-receivers-top.1db52c1c.png"
            class="w-full md:flex-1 md:min-w-0 h-auto"
            alt="The two tested USB GPS receivers side by side: the GlobalSat BU-353NC and the V-FAN UG-353"
          />
        </picture>
        <picture class="contents">
          <source
            srcset="/images/pro-features/gps-receivers-labels.e24ce8de.webp"
            type="image/webp"
          />
          <img
            decoding="async"
            loading="lazy"
            width="1024"
            height="582"
            src="/images/pro-features/gps-receivers-labels.e24ce8de.png"
            class="w-full md:flex-1 md:min-w-0 h-auto"
            alt="The underside labels of both receivers, showing model BU-353NC and V-FAN USB GPS Receiver model UG-353"
          />
        </picture>
      </div>
      <p class="text-center text-[15px] md:text-[16px] italic">
        The two USB GPS receivers tested with the recorder
      </p>

      <h4 class="text-white text-lg font-russo">
        Connecting Rally Navigator to GPS Devices
      </h4>
      <ol
        class="list-decimal pl-8 text-left py-2 flex flex-col gap-3 font-arial text-[16px] md:text-[18px] font-bold"
      >
        <li>
          Plug in the GPS puck to your computer with clear view of the sky
          (dashboard works fine).
        </li>
        <li>
          Wait about a minute to acquire GPS signal and click the Connect
          button.
        </li>
        <li>It may take 30 seconds to connect.</li>
        <li>
          Once the GPS puck is connected, you should see a green GPS indicator
          if you&rsquo;re receiving good GPS signal. You should also see the
          blue dot on the map at your location.
        </li>
      </ol>
      <picture class="contents">
        <source
          srcset="/images/pro-features/gps-recorder-app.cd7ccd20.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="1093"
          height="663"
          src="/images/pro-features/gps-recorder-app.cd7ccd20.png"
          class="block mx-auto w-full max-w-[1100px] h-auto"
          alt="GPS Roadbook Recorder selected in the File menu, with the USB GPS connection bar showing Connect, live speed and Start Recording"
        />
      </picture>
      <p class="text-[17px] md:text-[20px]">
        GPS connected and ready to record. Green dot indicates good GPS signal.
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/gps-connected-ready.f03f29d0.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="443"
          height="75"
          src="/images/pro-features/gps-connected-ready.f03f29d0.png"
          class="flex m-auto"
          alt="The recorder bar showing Disconnect, a green GPS status dot, current speed and Start Recording"
        />
      </picture>
      <picture class="contents">
        <source
          srcset="/images/pro-features/gps-recording-waypoints.984c6caf.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="1470"
          height="956"
          src="/images/pro-features/gps-recording-waypoints.984c6caf.png"
          class="block mx-auto w-full max-w-[1100px] h-auto"
          alt="A recording in progress: the track drawn on the map with numbered waypoints, elapsed time, distance and speed on the recorder bar, and the roadbook entries building alongside"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        Recording a roadbook. Click the New Waypoint button or tap the space bar
        at each location you want a Waypoint
      </p>
      <p class="text-[17px] md:text-[20px]">
        Tapping Start Recording creates your first waypoint. User can pause or
        stop the recording as needed. User can continue drawing the roadbook
        manually. If you already have an existing roadbook you would like to
        continue, you can load that roadbook file and continue recording with
        the GPS. User has full editing capability of any waypoint while
        recording a roadbook. When you are done editing a waypoint, clicking the
        target button on the recorder toolbar will fly the map back to your
        current location.
      </p>
      <hr class="border-0 border-t border-white/50 my-2" />

      <h3
        id="gps-validator"
        class="text-white text-xl font-russo scroll-mt-[90px] md:scroll-mt-4"
      >
        GPS Roadbook Validator
      </h3>
      <p class="text-[17px] md:text-[20px]">
        The GPS Roadbook Validator is used to review and update existing routes.
        Load an existing RN2 roadbook, connect the GPS antenna, and turn on the
        roadbook validator feature. Once you have connected, the blue dot will
        appear on the map at your location. The map and roadbook will
        automatically advance as you drive along the existing route, and you can
        easily edit existing waypoints along the way. If you need to add an
        additional waypoint at your location, tap the space bar. You can then
        edit the waypoint as needed. When you are done editing a waypoint,
        clicking the target button on the validator toolbar will fly the map
        back to your current location.
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/gps-validator.11e4cdc2.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="1176"
          height="713"
          src="/images/pro-features/gps-validator.11e4cdc2.png"
          class="block mx-auto w-full max-w-[1100px] h-auto"
          alt="The GPS Roadbook Validator following an existing route: a blue location dot on the drawn track, WP 9 on route in the toolbar, and an Add Waypoint button with its space bar shortcut"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        Validating an existing roadbook. Add a new waypoint by tapping Space bar
        or clicking the button on screen
      </p>
    </div>

    <!-- Waypoints and editing -->
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo">
        Waypoints &amp; Editing
      </h2>

      <h3 id="waypoint-map-icons" class="text-white text-xl font-russo scroll-mt-[90px] md:scroll-mt-4">
        Waypoint Numbers &amp; Control Icons on Map
      </h3>
      <p class="text-[17px] md:text-[20px]">
        Show waypoint numbers and control icons on the map. Checking a roadbook
        page against the map becomes direct, and the structure of the event
        stays visible while you plan the route.
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/waypoint-numbers-control-icons.48a98859.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="1184"
          height="718"
          src="/images/pro-features/waypoint-numbers-control-icons.48a98859.png"
          class="block mx-auto w-full max-w-[1100px] h-auto"
          alt="Show Waypoint Number on Map and Show Waypoint Control Icons both enabled, with numbered waypoints and DZ, stop and finish control icons drawn on the route"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        Waypoint numbers and control icons shown on the map
      </p>
      <hr class="border-0 border-t border-white/50 my-2" />

      <h3 id="smart-icons" class="text-white text-xl font-russo scroll-mt-[90px] md:scroll-mt-4">Drag and Drop Smart Icons</h3>
      <p class="text-[17px] md:text-[20px]">
        Drag and drop QR code for latitude, longitude and elevation data into
        any waypoint.
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/smart-icons-drag-drop.3753be92.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="549"
          height="367"
          src="/images/pro-features/smart-icons-drag-drop.3753be92.png"
          class="flex m-auto"
          alt="The ELEV and GPS smart icons dragged from the icon toolbar into a waypoint, producing an elevation readout and a GPS QR code in the roadbook cell"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        Drag the ELEV and GPS smart icons into a waypoint
      </p>
      <hr class="border-0 border-t border-white/50 my-2" />

      <h3 id="track-distance-hover" class="text-white text-xl font-russo scroll-mt-[90px] md:scroll-mt-4">Hover Over Track Distance</h3>
      <p class="text-[17px] md:text-[20px]">
        Hovering over a track point will show the user:
      </p>
      <ul
        class="flex flex-row flex-wrap justify-center gap-x-10 gap-y-2 py-2 font-arial text-[16px] md:text-[18px] font-bold"
      >
        <li>Distance from Start</li>
        <li>Distance from Previous Waypoint</li>
        <li>Distance to Finish</li>
      </ul>
      <picture class="contents">
        <source
          srcset="/images/pro-features/et-hover-distances.d630b884.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="325"
          height="174"
          src="/images/pro-features/et-hover-distances.d630b884.png"
          class="flex m-auto"
          alt="Hovering a track point shows a readout of distance from start, from previous waypoint and to finish"
        />
      </picture>
      <hr class="border-0 border-t border-white/50 my-2" />

      <h3 id="et-distance" class="text-white text-xl font-russo scroll-mt-[90px] md:scroll-mt-4">
        Add Track Distance/CAP
      </h3>
      <p class="text-[17px] md:text-[20px]">
        Adding Track Distance info to a Waypoint: right-click on a subsequent
        track point for the option to add distance and an icon, usually a
        Terrain icon or a "!". Select it in the menu, then drag and drop the
        icon into the notes box. It will automatically add itself to the
        @ Distance display and become a single draggable, resizable object.
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/track-distance-menu.07c68c63.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="1142"
          height="527"
          src="/images/pro-features/track-distance-menu.07c68c63.png"
          class="block mx-auto w-full max-w-[1100px] h-auto"
          alt="Right-clicking a track point opens Add Track Distance/CAP with @ Distance, @ Distance + ICON, ET DISTANCE CAP, CAPa and CAPc, alongside the roadbook entries they produce"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        The Add Track Distance/CAP menu on a track point
      </p>
      <p class="text-[17px] md:text-[20px]">
        You can also add ET Distance and CAP, CAPa or CAPc to the waypoint:
      </p>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-3 font-arial text-[16px] md:text-[18px] font-bold"
      >
        <li>CAP (compass heading immediately leaving this track point)</li>
        <li>CAPa (the average compass heading to the next waypoint)</li>
        <li>CAPc (machine calculated CAP heading to the next waypoint)</li>
      </ul>
      <picture class="contents">
        <source
          srcset="/images/pro-features/track-distance-at-35m.be8df4c6.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="844"
          height="396"
          src="/images/pro-features/track-distance-at-35m.be8df4c6.png"
          class="flex m-auto"
          alt="A track distance of @ 35m placed with its icon, shown both on the map and in the roadbook cell"
        />
      </picture>
      <picture class="contents">
        <source
          srcset="/images/pro-features/track-distance-et-cap.13734bf4.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="905"
          height="393"
          src="/images/pro-features/track-distance-et-cap.13734bf4.png"
          class="flex m-auto"
          alt="The same waypoint carrying both @ 35m and an ET 70m distance with a 314A CAP heading"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        A track distance and an ET distance with its CAP heading
      </p>
      <p class="text-[17px] md:text-[20px]">
        You can also drag the track point and watch the distance and CAP
        information update dynamically.
      </p>
    </div>

    <!-- Export -->
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2
        id="kmz-gpx-export"
        class="text-white text-2xl md:text-3xl font-russo scroll-mt-[90px] md:scroll-mt-4"
      >
        KMZ and GPX Export with Route Details
      </h2>
      <p class="text-[17px] md:text-[20px]">
        Export the route as a KMZ file with details included for:
      </p>
      <ul
        class="flex flex-row flex-wrap justify-center gap-x-10 gap-y-2 py-2 font-arial text-[16px] md:text-[18px] font-bold"
      >
        <li>Special stage</li>
        <li>Transit stage</li>
        <li>Speed zones</li>
        <li>Neutralizations, waypoints, control icons, etc.</li>
      </ul>
      <p class="text-[17px] md:text-[20px]">
        Useful for rally planning, event monitoring, safety, dispatch, media,
        staff and asset location.
      </p>
      <p class="text-[17px] md:text-[20px]">
        Users can quickly click and fly to any item on the list, with the
        included details visible.
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/kmz-fly-to-item.c7780049.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="1470"
          height="956"
          src="/images/pro-features/kmz-fly-to-item.c7780049.png"
          class="block mx-auto w-full max-w-[1100px] h-auto"
          alt="Clicking a waypoint in the Google Earth places list flies to it and opens its details: waypoint and control point numbers, distance, coordinates and elevation"
        />
      </picture>
      <p class="text-[17px] md:text-[20px] text-center">
        <a
          href="/create-cross-country/Cross_Country_Rally_Sample.kmz"
          target="_blank"
          rel="noopener"
          class="text-[#FF0000] hover:underline"
          ><strong>Download the sample KMZ &mdash; Cross Country Rally</strong></a
        >
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/gpx-basecamp.eeefbef6.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="1470"
          height="956"
          src="/images/pro-features/gpx-basecamp.eeefbef6.png"
          class="block mx-auto w-full max-w-[1100px] h-auto"
          alt="The exported GPX open in Garmin BaseCamp, listing every CCRR waypoint with its name and showing the route on the map"
        />
      </picture>
      <p class="text-[17px] md:text-[20px] text-center">
        <a
          href="/create-cross-country/Cross_Country_Rally_Sample-Route.gpx"
          target="_blank"
          rel="noopener"
          class="text-[#FF0000] hover:underline"
          ><strong>Download the sample GPX &mdash; Cross Country Rally route</strong></a
        >
      </p>
    </div>

    <!-- Tulip Assist -->
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo">Tulip Assist</h2>

      <h3
        id="tulip-assist"
        class="text-white text-xl font-russo scroll-mt-[90px] md:scroll-mt-4"
      >
        Tulip Assist Is a Starting Point &mdash; Review Required
      </h3>
      <p class="text-[17px] md:text-[20px]">
        Tulip Assist helps you draw tulips from digital road maps and satellite
        imagery.
      </p>
      <p class="text-[17px] md:text-[20px]">
        There are three phases of Tulip Assist that display in the toolbar at
        the top of the roadbook panel: Preparing, Generating, Reviewing.
      </p>
      <p class="text-[17px] md:text-[20px]">
        Use the Follow Roads feature to lay out your route. Some waypoints will
        be automatically added. You can manually create or hide additional
        waypoints along the way. Let Tulip Assist Prepare and Generate Tulips
        while you build your route and waypoints. Then go back to waypoint number
        one and review each waypoint, correcting the tulip and adding icons and
        text as needed. When you are done reviewing a tulip, clicking the red A
        button marks it approved, turning it to a green check and advancing to
        the next waypoint. You can print a roadbook anytime, even if all the
        waypoints have not been reviewed and approved.
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/tulip-assist-generating.d5492c0f.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="1091"
          height="661"
          src="/images/pro-features/tulip-assist-generating.d5492c0f.png"
          class="block mx-auto w-full max-w-[1100px] h-auto"
          alt="Tulip Assist switched on in the Roadbook menu, with Preparing 0 of 11 running in the toolbar and drafted tulips appearing in the roadbook"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        Turn Tulip Assist on from the Roadbook menu
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/tulip-assist-explainer.42d38003.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="617"
          height="529"
          src="/images/pro-features/tulip-assist-explainer.42d38003.png"
          class="flex m-auto"
          alt="The in-app Tulip Assist panel explaining how it reads the road network and satellite imagery, and why the result will not be perfect"
        />
      </picture>
      <picture class="contents">
        <source
          srcset="/images/pro-features/tulip-assist-preparing.3ba5b1ec.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="537"
          height="382"
          src="/images/pro-features/tulip-assist-preparing.3ba5b1ec.png"
          class="flex m-auto"
          alt="Preparing 2 of 7 with a progress bar in the toolbar and Reviewed 0 of 2 beneath it, while the drafted tulips are marked in the roadbook"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        The Preparing phase, with its progress bar and review counter
      </p>
      <picture class="contents">
        <source
          srcset="/images/pro-features/tulip-assist-review-progress.da5b631e.webp"
          type="image/webp"
        />
        <img
          decoding="async"
          loading="lazy"
          width="535"
          height="503"
          src="/images/pro-features/tulip-assist-review-progress.da5b631e.png"
          class="flex m-auto"
          alt="Reviewed 2 of 13, with a green check on the reviewed tulip and red A badges still on the ones below it"
        />
      </picture>
      <p class="text-center text-[15px] md:text-[16px] italic">
        Reviewed tulips turn green; Review Next steps through the rest
      </p>
    </div>

    <!-- CTA -->
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="text-white text-2xl md:text-3xl font-russo">
        Get Pro Rally
      </h2>
      <p class="text-[17px] md:text-[20px]">
        Pro Rally includes everything in the Premium tier plus all of the
        features above. Download Rally Navigator to try it.
      </p>
      <router-link to="/download" class="m-auto">
        <h2
          class="text-[#FF0000] font-russo text-[22px] md:text-[28px] hover:underline text-center"
        >
          Download Rally Navigator and Create Your First Roadbook Free
        </h2>
      </router-link>
    </div>
  </section>

  <section class="my-8 mx-auto max-w-[1400px]">
    <FeaturesBanner />
    <PartnersSmall />
    <RallyLogoDownload link-text="Download Rally Navigator" />
  </section>
  <RelatedGuides
    :links="[
      { to: '/rally-roadbook', text: 'What Is a Rally Roadbook?' },
      { to: '/how-to-create-a-cross-country', text: 'How to Create a Cross Country Roadbook' },
      { to: '/how-to-create-a-road-rally', text: 'How to Create a Road Rally Roadbook' },
      { to: '/timing-scoring', text: 'Rally Timing &amp; Scoring' },
    ]"
  />
</template>

<script>
import FeaturesBanner from './FeaturesBanner.vue';
import RelatedGuides from './RelatedGuides.vue';
import PartnersSmall from './PartnersSmall.vue';
import RallyLogoDownload from './RallyLogoDownload.vue';
import ProRallyCard from './ProRallyCard.vue';

export default {
  name: 'ProFeatures',
  components: {
    FeaturesBanner,
    RelatedGuides,
    PartnersSmall,
    RallyLogoDownload,
    ProRallyCard,
  },
};
</script>

<style scoped>
/* Render the Pro card the way the desktop app's upgrade modal does.
   Values taken from UpgradeOptions.vue: .option-wide, .feature-list-grid
   .feature, and .feature-link. */
/* Mirrors the desktop app's Upgrade panel. PR #386 widened the Pro card
   440px -> 640px and moved the feature grid from 2 columns to 3 so the whole
   card fits without scrolling; this keeps the replica honest. */
.app-card {
  width: 640px;
  max-width: 100%;
}

.app-card :deep(.perks-two-col) {
  grid-template-columns: 1fr 1fr 1fr;
}

/* The 2-col striping in ProRallyCard assumes pairs; with 3 columns the rows
   are groups of 3, so restripe by row here. */
.app-card :deep(.perks-two-col li:nth-child(6n + 1)),
.app-card :deep(.perks-two-col li:nth-child(6n + 2)),
.app-card :deep(.perks-two-col li:nth-child(6n + 3)) {
  background-color: #393a44;
}

.app-card :deep(.perks-two-col li:nth-child(6n + 4)),
.app-card :deep(.perks-two-col li:nth-child(6n + 5)),
.app-card :deep(.perks-two-col li:nth-child(6n)) {
  background-color: #43445180;
}

@media (max-width: 700px) {
  .app-card :deep(.perks-two-col) {
    grid-template-columns: 1fr 1fr;
  }
}

.app-card :deep(.card-featured) {
  border: 2px solid #ff0000;
  border-radius: 8px;
  box-shadow: 0 0 12px rgba(255, 0, 0, 0.3);
  padding-bottom: 20px;
  overflow: hidden;
}

.app-card :deep(.perks-two-col li),
.app-card :deep(.perks li) {
  font-size: 11px;
  line-height: 1.35;
  padding: 6px 8px;
  background-color: #393a44;
}

.app-card :deep(.perks-heading--block) {
  font-size: 11px;
  padding: 6px 8px;
}

.app-card :deep(.perk-link) {
  text-decoration: underline dotted;
  text-underline-offset: 2px;
}

.app-card :deep(.perk-link:hover),
.app-card :deep(.perk-link:focus-visible) {
  color: #ff0000;
  text-decoration: underline dotted;
}
</style>
